import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionSharpIcon from '@mui/icons-material/DescriptionSharp';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LanguageIcon from '@mui/icons-material/Language';
import MapSharpIcon from '@mui/icons-material/MapSharp';
import MenuIcon from '@mui/icons-material/Menu';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { Box, Fab, IconButton, Popover, Tooltip, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeSlug from "rehype-slug-custom-id";
import remarkGfm from "remark-gfm";
import remarkUnwrapImages from 'remark-unwrap-images';
import Menu from "./Components/Menu";
import MenuButton from "./Components/MenuButton";
import Topbar from "./Components/Topbar";
import contentDa from "./Contents/brugerguide_da.md";
import contentEn from "./Contents/user_guide_en.md";
import indexDa from "./Contents/indexDa.md";
import indexEn from "./Contents/indexEn.md";
import tekniskReferenceDa from "./Contents/teknisk_reference_da.md";
import technicalReferenceEn from "./Contents/technical_reference_en.md";


export default function MainPage() {

    const [contentText, setContentText] = useState("")
    const [menu, setMenu] = useState("")
    const [language, setLanguage] = useState<'da' | 'en'>('da');

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLanguageChange = (event: React.MouseEvent<HTMLElement>, newLanguage: 'da' | 'en' | null) => {
        if (newLanguage !== null) {
            setLanguage(newLanguage);
        }
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    useEffect(() => {
        const indexFile = language === 'da' ? indexDa : indexEn;
        
        // Load all content files for the selected language and combine them
        const contentFiles = language === 'da' 
            ? [contentDa, tekniskReferenceDa]
            : [contentEn, technicalReferenceEn];

        // Fetch all content files and combine them
        Promise.all(contentFiles.map(file => fetch(file).then(res => res.text())))
            .then(texts => {
                // Combine all texts with a separator
                const combinedContent = texts.join('\n\n---\n\n');
                setContentText(combinedContent);
            });

        fetch(indexFile)
            .then(res => res.text())
            .then(text => setMenu(text));
    }, [language]);

    return (
        <Box
            className="app"
            display='flex'
            flexDirection='column'
            sx={{
                height: '100vh',
            }}
        >
            <Topbar title={"BMSnet Documentation"}
                logo=
                {
                    <IconButton onClick={handleClick} >
                        <MenuIcon sx={{ color: "#ffffff" }} />
                    </IconButton>
                }
                color={'#2653a2'}
            />
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Menu width='200px' sx={{ marginRight: '0px' }}>
                    <MenuButton
                        text="Forside"
                        onclick={() => {
                            window.location.replace(window.location.origin);
                        }}
                        icon={<HomeIcon color='primary' />}
                    />
                    <MenuButton
                        text="Dashboard"
                        onclick={() => {
                            window.location.replace(window.location.origin + "/dashboard/");
                        }}
                        icon={<DashboardIcon color='primary' />}
                    />
                    <MenuButton
                        text="Alarmer"
                        onclick={() => {
                            window.location.replace("https://alarm.bmsnet.online");
                        }}
                        icon={<AccessAlarmIcon color='primary' />}
                    />
                    <MenuButton
                        text="Diagrammer"
                        onclick={() => {
                            window.location.replace(window.location.origin + "/view/");
                        }}
                        icon={<ShowChartIcon color='primary' />}
                    />
                    <MenuButton
                        text="Kort"
                        onclick={() => {
                            window.location.replace(window.location.origin + "/maps/");
                        }}
                        icon={<MapSharpIcon color='primary' />}
                    />
                    <MenuButton
                        text="Administration"
                        onclick={() => {
                            window.location.replace(window.location.origin + "/admin/");
                        }}
                        icon={<AdminPanelSettingsIcon color='primary' />}
                    />
                    <MenuButton
                        text="Dokumentation"
                        onclick={() => {
                            window.location.replace(window.location.origin + "/documentation/");
                        }}
                        icon={<DescriptionSharpIcon color='primary' />}
                    />
                </Menu>
            </Popover>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexGrow: 1,
                    overflow: 'hidden',
                }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: 450,
                        height: '100%',
                        borderRight: '1px solid #e0e0e0',
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#888',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: '#555',
                        },
                    }}>
                        <ReactMarkdown
                            children={menu}
                            className="sideMenu"
                        />
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        padding: 1,
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: '#fafafa',
                        flexShrink: 0,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, paddingLeft: 0.5 }}>
                            <LanguageIcon sx={{ fontSize: 16, color: '#666' }} />
                            <Typography sx={{ fontSize: 11, color: "#666", fontWeight: 500 }}>
                                Language / Sprog
                            </Typography>
                        </Box>
                        <ToggleButtonGroup
                            value={language}
                            exclusive
                            onChange={handleLanguageChange}
                            size="small"
                            fullWidth
                            sx={{
                                '& .MuiToggleButton-root': {
                                    fontSize: '0.75rem',
                                    padding: '4px 8px',
                                    textTransform: 'none',
                                }
                            }}
                        >
                            <ToggleButton value="da">
                                Dansk
                            </ToggleButton>
                            <ToggleButton value="en">
                                English
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: "#999999",
                                textAlign: 'center'
                            }}
                        >
                            BMSnet Documentation
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexGrow: 1,
                    overflow: 'auto',
                }}>
                    <Box
                        className='content'
                        pl={2}
                        pr={2}
                        pt={3}
                        sx={{
                            display: 'flex',
                            flexDirection: "column",
                            flexGrow: 1,
                            overflow: 'auto',
                        }}>
                        <ReactMarkdown
                            children={contentText}
                            remarkPlugins={[remarkGfm, remarkUnwrapImages]}
                            rehypePlugins={[
                                rehypeHighlight,
                                rehypeSlug,
                            ]}
                            transformImageUri={(uri) => {
                                // If it's already an absolute URL, return as-is
                                if (uri.startsWith("http://") || uri.startsWith("https://")) {
                                    return uri;
                                }
                                
                                // Use process.env.PUBLIC_URL to get the base path from package.json homepage
                                const basePath = process.env.PUBLIC_URL || '';
                                
                                // Remove leading ./ or / from uri
                                const cleanUri = uri.replace(/^\.?\//, '');
                                
                                // Construct the full path
                                return `${basePath}/${cleanUri}`;
                            }}
                        />
                    </Box>
                    <Box
                        position='fixed'
                        bottom='16px'
                        right='28px'
                        zIndex={10}
                    >
                        <Tooltip title="Scroll to top" placement='left'>
                            <Fab
                                color="primary"
                                onClick={() => {
                                    const contentElement = document.querySelector('.content');
                                    if (contentElement) {
                                        contentElement.scrollTop = 0;
                                    }
                                }}
                                size='small'
                            >
                                <KeyboardArrowUpIcon />
                            </Fab>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}